/**
 * PDFReaderContext provides centralized state management for PDF annotation features.
 *
 * This context replaces the previous prop-drilling pattern where useSimpleState's
 * state and setters were passed through PDFReader → PDFViewport → PDFPageInteractive
 * and further down to TextAreaContainer and LineContainer.
 *
 * State managed:
 * - mode: Current annotation mode (hand, type, line, pencil, eraser)
 * - activeTextBox: Currently selected text annotation
 * - activeLine: Currently selected line annotation
 * - gradeModalOpen: Grade modal visibility state
 * - documentLoaded: PDF document loading state
 */

import React, { createContext, useContext, useReducer, useCallback, ReactNode } from 'react';
import { WIPMode } from '@punchcard/core/constants/Wip';

// Just the state we want to manage
interface PDFReaderState {
	mode: WIPModeType;
	activeTextBox: { page: number; id: string } | null;
	activeLine: { page: number; id: string } | null;
	gradeModalOpen: boolean;
	documentLoaded: boolean;
}

// Simple actions - just say what you want to do
type Action =
	| { type: 'SET_MODE'; mode: WIPModeType }
	| { type: 'SET_ACTIVE_TEXTBOX'; textbox: { page: number; id: string } | null }
	| { type: 'SET_ACTIVE_LINE'; line: { page: number; id: string } | null }
	| { type: 'SET_GRADE_MODAL'; open: boolean }
	| { type: 'SET_DOCUMENT_LOADED'; loaded: boolean };

// Actions and state combined in context value
interface PDFReaderContextValue extends PDFReaderState {
	setMode: (mode: WIPModeType | ((prev: WIPModeType) => WIPModeType)) => void;
	setActiveTextBox: (textbox: { page: number; id: string } | null | ((prev: { page: number; id: string } | null) => { page: number; id: string } | null)) => void;
	setActiveLine: (line: { page: number; id: string } | null | ((prev: { page: number; id: string } | null) => { page: number; id: string } | null)) => void;
	setGradeModalOpen: (open: boolean | ((prev: boolean) => boolean)) => void;
	setDocumentLoaded: (loaded: boolean | ((prev: boolean) => boolean)) => void;
}

// Simple reducer - just handle each action
function reducer(state: PDFReaderState, action: Action): PDFReaderState {
	switch (action.type) {
		case 'SET_MODE':
			return {
				...state,
				mode: action.mode,
				// Smart cleanup: clear textbox/line when mode changes
				activeTextBox: action.mode !== WIPMode.Type ? null : state.activeTextBox,
				activeLine: action.mode !== WIPMode.Line ? null : state.activeLine,
			};

		case 'SET_ACTIVE_TEXTBOX':
			return {
				...state,
				activeTextBox: action.textbox,
				activeLine: action.textbox ? null : state.activeLine, // Clear line when textbox is set
			};

		case 'SET_ACTIVE_LINE':
			return {
				...state,
				activeLine: action.line,
				activeTextBox: action.line ? null : state.activeTextBox, // Clear textbox when line is set
			};

		case 'SET_GRADE_MODAL':
			return { ...state, gradeModalOpen: action.open };

		case 'SET_DOCUMENT_LOADED':
			return { ...state, documentLoaded: action.loaded };

		default:
			return state;
	}
}

// Create the context
const PDFReaderContext = createContext<PDFReaderContextValue | undefined>(undefined);

// Provider component
interface PDFReaderProviderProps {
	children: ReactNode;
}

export const PDFReaderProvider: React.FC<PDFReaderProviderProps> = ({ children }) => {
	const [state, dispatch] = useReducer(reducer, {
		mode: WIPMode.Hand,
		activeTextBox: null,
		activeLine: null,
		gradeModalOpen: false,
		documentLoaded: false,
	});

	// Make setters compatible with React setState pattern (handle both values and functions)
	const setMode = useCallback((mode: WIPModeType | ((prev: WIPModeType) => WIPModeType)) => {
		if (typeof mode === 'function') {
			dispatch({ type: 'SET_MODE', mode: mode(state.mode) });
		} else {
			dispatch({ type: 'SET_MODE', mode });
		}
	}, [state.mode]);

	const setActiveTextBox = useCallback((
		textbox: { page: number; id: string } | null | ((prev: { page: number; id: string } | null) => { page: number; id: string } | null)
	) => {
		if (typeof textbox === 'function') {
			dispatch({ type: 'SET_ACTIVE_TEXTBOX', textbox: textbox(state.activeTextBox) });
		} else {
			dispatch({ type: 'SET_ACTIVE_TEXTBOX', textbox });
		}
	}, [state.activeTextBox]);

	const setActiveLine = useCallback((
		line: { page: number; id: string } | null | ((prev: { page: number; id: string } | null) => { page: number; id: string } | null)
	) => {
		if (typeof line === 'function') {
			dispatch({ type: 'SET_ACTIVE_LINE', line: line(state.activeLine) });
		} else {
			dispatch({ type: 'SET_ACTIVE_LINE', line });
		}
	}, [state.activeLine]);

	const setGradeModalOpen = useCallback((open: boolean | ((prev: boolean) => boolean)) => {
		if (typeof open === 'function') {
			dispatch({ type: 'SET_GRADE_MODAL', open: open(state.gradeModalOpen) });
		} else {
			dispatch({ type: 'SET_GRADE_MODAL', open });
		}
	}, [state.gradeModalOpen]);

	const setDocumentLoaded = useCallback((loaded: boolean | ((prev: boolean) => boolean)) => {
		if (typeof loaded === 'function') {
			dispatch({ type: 'SET_DOCUMENT_LOADED', loaded: loaded(state.documentLoaded) });
		} else {
			dispatch({ type: 'SET_DOCUMENT_LOADED', loaded });
		}
	}, [state.documentLoaded]);

	const value: PDFReaderContextValue = {
		...state,
		setMode,
		setActiveTextBox,
		setActiveLine,
		setGradeModalOpen,
		setDocumentLoaded,
	};

	return (
		<PDFReaderContext.Provider value={value}>
			{children}
		</PDFReaderContext.Provider>
	);
};

// Hook to use the context
export const usePDFReaderContext = (): PDFReaderContextValue => {
	const context = useContext(PDFReaderContext);
	if (context === undefined) {
		throw new Error('usePDFReaderContext must be used within a PDFReaderProvider');
	}
	return context;
};

// Export the context for testing purposes (optional)
export { PDFReaderContext };
